import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Building, Pencil } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreateCompanyDialog } from "@/components/companies/CreateCompanyDialog";
import { EditCompanyDialog } from "@/components/companies/EditCompanyDialog";
import { CursorPager, type Cursors } from "@/components/ui/cursor-pager";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";

type CompanyResponse = components["schemas"]["CompanyResponse"];

const PAGE_SIZE = 50;
const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });

export function CompaniesPage() {
  const navigate = useNavigate();
  const { activeClientId } = useAuth();
  const [companies, setCompanies] = useState<CompanyResponse[] | null>(null);
  const [cursors, setCursors] = useState<Cursors | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [editing, setEditing] = useState<CompanyResponse | null>(null);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/v1/companies", {
        params: { query: { Limit: PAGE_SIZE, ...(cursor ? { Cursor: cursor } : {}) } },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setError("Couldn't load companies.");
          return;
        }
        setCompanies(data?.data ?? []);
        setCursors(data?.meta?.cursors ?? null);
      })
      .catch(() => {
        if (!cancelled) setError("Network error fetching companies.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeClientId, cursor, reloadTick]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Companies</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            A company groups the organisations of one multi-site business. Bills addressed to the group
            rather than to a specific site are received here and routed to the right organisation.
            Organisations that are not part of a group do not need one.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} disabled={!activeClientId}>
          <Plus className="size-3.5" /> New company
        </Button>
      </div>

      {activeClientId && (
        <CreateCompanyDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => {
            setCursor(null);
            setReloadTick((n) => n + 1);
          }}
        />
      )}

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading companies...
        </div>
      ) : (companies?.length ?? 0) === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-center">
          <Building className="size-6 text-muted-foreground" />
          <p className="text-sm font-medium">No companies yet</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Create one for a franchise or multi-site business whose bills arrive addressed to the group.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Company</TableHead>
                <TableHead>Head office</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(companies ?? []).map((c) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => c.id && navigate(`/companies/${c.id}`)}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{c.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">{c.id}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[c.addressLine1, c.city, c.postalCode].filter(Boolean).join(", ") || "Not set"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.contactEmail ?? "Not set"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.createdAt ? dateFmt.format(new Date(c.createdAt)) : "Not set"}
                  </TableCell>
                  {/* stopPropagation: the row itself navigates to the company. */}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="ghost"
                      title={`Edit ${c.name}`}
                      onClick={() => setEditing(c)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Patched in place rather than re-fetching: the PATCH response is the updated company, and
          reloading would also reset the page the user is on. */}
      <EditCompanyDialog
        company={editing}
        open={editing !== null}
        onOpenChange={(next) => !next && setEditing(null)}
        onSaved={(updated) =>
          setCompanies((prev) => prev?.map((c) => (c.id === updated.id ? updated : c)) ?? prev)
        }
      />

      {!error && !loading && companies && companies.length > 0 && (
        <CursorPager cursors={cursors} onNavigate={setCursor} />
      )}
    </div>
  );
}
